/**
 * @fileoverview Elevated 3-pane System Dashboard Template Builder page (`SystemDashboardBuilderPage`)
 * for `system-web`. Mirrors `SystemDocumentBuilderPage` and `SystemDataReportBuilderPage` and consumes
 * the exact same shared `DashboardCanvasBuilder` from `@firesquirrel/ui-admin` as `DashboardBuilderPage`.
 * @module system-web/pages/reports/SystemDashboardBuilderPage
 *
 * Responsibilities: - Render the 3-pane system dashboard builder shell:
 *                     1. Left pane: widget activation checklist (`widgetActivationList`) + settings
 *                        accordions (`Library & Visibility` [`isInLibrary`, `visibility`, `allowedTenantIds`],
 *                        `Auto-Refresh`, `Theme & Branding`).
 *                     2. Center pane: shared `DashboardCanvasBuilder` from `@firesquirrel/ui-admin`.
 *                     3. Right pane: `Live Preview` (with `Preview Department` selector backed by
 *                        `watchTenants()` and `systemDashboardDataService`) and `Properties` (portal target).
 *                   - Render read-only `view` and `fullscreen` modes.
 *                   - Persist system dashboard definitions and library visibility locally via
 *                     `systemReportDefinitionService`.
 * Dependencies:     - @firesquirrel/ui-admin (DashboardCanvasBuilder, DashboardThemeEditor, GridSetupPanel,
 *                     DocumentLifecycleControls, BuilderHelpPanel, GuidedTour, AccordionSection,
 *                     VisibilityBadge, WidgetRenderer, widgetActivationList, setWidgetEnabled,
 *                     normalizeDashboardConfig, DEFAULT_DASHBOARD_CONFIG, DASHBOARD_BUILDER_TOUR_STEPS,
 *                     DASHBOARD_TOUR_TARGET, usePersistedNumber, BUILDER_HELP_SECTIONS)
 *                   - ../../services/systemReportDefinitionService (Dexie CRUD + `toggleLibrary` + `setReportDefinitionVisibility`)
 *                   - ../../services/systemDashboardDataService (`resolveSystemDashboardPreviewData`, `SAMPLE_PREVIEW_DEPARTMENT_ID`)
 *                   - ../../services/tenantService (`watchTenants`)
 * Architecture:     - Local-first: all reads and writes go through `getSystemDb()` services; zero direct API calls.
 *
 * @localFirst
 * Source of Truth: local (Dexie `report_definitions`, `tenants` in `firesquirrel-system`)
 * Write Flow: Dexie write via `systemReportDefinitionService` → `wakeLocalWrite('report_definitions')` → background sync
 * Sync Strategy: Optimistic write; background replication
 * Conflict Resolution: Last-write-wins (`updatedAt`)
 *
 * Notes:            - Closes gaps G-42, G-47, G-49, G-51, and G-52.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  HelpCircle,
  Compass,
  Settings,
  Play,
  Maximize2,
  X,
  Globe,
  PanelRightClose,
  PanelRightOpen,
} from 'lucide-react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import type {
  ReportDefinitionLocal,
  DashboardConfig,
  SystemLibraryVisibility,
} from '@firesquirrel/local-db';
import {
  getReportDefinition,
  updateReportDefinition,
  createReportDefinition,
  publishReportDefinition,
  archiveReportDefinition,
  toggleLibrary,
  setReportDefinitionVisibility,
} from '../../services/systemReportDefinitionService';
import {
  resolveSystemDashboardPreviewData,
  SAMPLE_PREVIEW_DEPARTMENT_ID,
} from '../../services/systemDashboardDataService';
import { watchTenants } from '../../services/tenantService';
import {
  DashboardCanvasBuilder,
  DashboardThemeEditor,
  GridSetupPanel,
  DocumentLifecycleControls,
  BuilderHelpPanel,
  GuidedTour,
  AccordionSection,
  VisibilityBadge,
  WidgetRenderer,
  widgetActivationList,
  setWidgetEnabled,
  normalizeDashboardConfig,
  DEFAULT_DASHBOARD_CONFIG,
  DASHBOARD_BUILDER_TOUR_STEPS,
  DASHBOARD_TOUR_TARGET,
  usePersistedNumber,
  type FilterValue,
  type DocumentTemplateStatus,
} from '@firesquirrel/ui-admin';

/**
 * @contract SystemDashboardBuilderPageProps
 * Purpose: Props for `SystemDashboardBuilderPage`.
 * Fields:  mode?
 * Rules:   `mode` defaults to `'edit'`.
 * Sync:    N/A.
 */
export interface SystemDashboardBuilderPageProps {
  mode?: 'edit' | 'view' | 'fullscreen';
}

interface PreviewTenantItem {
  _id: string;
  name: string;
}

/**
 * @component SystemDashboardBuilderPage
 * Purpose:     3-pane system dashboard template editor and viewer for `system-web`.
 * Props:       SystemDashboardBuilderPageProps.
 * Behavior:    Loads system dashboard from Dexie; provides 3-pane editing with `Library & Visibility`
 *              accordion and `Preview Department` selector.
 * Data Source: local (`getSystemDb()` via `systemReportDefinitionService`, `watchTenants()`, and `systemDashboardDataService`).
 * Constraints: no direct API calls.
 * Performance: preview rows resolved locally on config or preview department change.
 */
export default function SystemDashboardBuilderPage({
  mode = 'edit',
}: SystemDashboardBuilderPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumbLabel } = useBreadcrumbs();

  const isNew = !id || id === 'new';
  const isViewMode = mode === 'view' || mode === 'fullscreen';
  const isFullscreen = mode === 'fullscreen';

  const aiDashboardData = location.state as { aiGenerated?: boolean; previewConfig?: any } | null;
  const [isAIGenerated, setIsAIGenerated] = useState<boolean>(
    Boolean(aiDashboardData?.aiGenerated),
  );

  const [dashboard, setDashboard] = useState<ReportDefinitionLocal | null>(null);
  const [dashboardConfig, setDashboardConfig] = useState<DashboardConfig>(DEFAULT_DASHBOARD_CONFIG);
  const [dashboardName, setDashboardName] = useState('Untitled Dashboard');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [status, setStatus] = useState<DocumentTemplateStatus>('draft');
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [visibility, setVisibility] = useState<SystemLibraryVisibility>('all-departments');
  const [allowedTenantIds, setAllowedTenantIds] = useState<string[]>([]);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<'preview' | 'properties'>('preview');
  const [rightPaneCollapsed, setRightPaneCollapsed] = useState(false);
  const [propertiesHost, setPropertiesHost] = useState<HTMLDivElement | null>(null);

  const [gridSetupOpen, setGridSetupOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  const [previewDepartments, setPreviewDepartments] = useState<PreviewTenantItem[]>([]);
  const [previewDepartmentId, setPreviewDepartmentId] = useState<string>(
    SAMPLE_PREVIEW_DEPARTMENT_ID,
  );
  const [widgetDataMap, setWidgetDataMap] = useState<Record<string, Record<string, unknown>[]>>({});

  const [globalFilters, setGlobalFilters] = useState<Record<string, FilterValue | null>>({});
  const [refreshKey, setRefreshKey] = useState(0);

  const [leftPanelWidth, setLeftPanelWidth] = usePersistedNumber('sys-dash-builder-panel-width', 272);
  const [rightPanelWidth, setRightPanelWidth] = usePersistedNumber('sys-dash-builder-preview-split', 384);

  const startResizeLeft = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = leftPanelWidth;
      const onMove = (ev: MouseEvent) => {
        setLeftPanelWidth(Math.min(420, Math.max(200, startWidth + (ev.clientX - startX))));
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [leftPanelWidth, setLeftPanelWidth],
  );

  const startResizeRight = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = rightPanelWidth;
      const onMove = (ev: MouseEvent) => {
        setRightPanelWidth(Math.min(600, Math.max(280, startWidth - (ev.clientX - startX))));
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [rightPanelWidth, setRightPanelWidth],
  );

  const activationEntries = useMemo(
    () => widgetActivationList(dashboardConfig),
    [dashboardConfig],
  );

  // Subscribe to tenants from system Dexie via watchTenants()
  useEffect(() => {
    try {
      const sub = watchTenants().subscribe({
        next: (rows: any[]) => {
          setPreviewDepartments(
            (rows ?? []).map((r) => ({
              _id: String(r._id),
              name: String(r.name ?? r._id),
            })),
          );
        },
        error: () => {
          // ignore in isolated unit tests
        },
      });
      return () => sub.unsubscribe();
    } catch {
      return undefined;
    }
  }, []);

  // Resolve local preview data when config, previewDepartmentId, or globalFilters change
  useEffect(() => {
    let cancelled = false;
    void resolveSystemDashboardPreviewData(dashboardConfig, previewDepartmentId, globalFilters).then(
      (map) => {
        if (!cancelled) setWidgetDataMap(map);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [dashboardConfig, previewDepartmentId, globalFilters, refreshKey]);

  // Load existing system dashboard or AI preview
  useEffect(() => {
    if (!isNew && id) {
      void loadDashboard(id);
    } else if (isNew && aiDashboardData?.previewConfig) {
      const config = aiDashboardData.previewConfig;
      setDashboardName(config.name || 'Untitled Dashboard');
      setDashboardDescription(config.description || '');
      if (config.dashboardConfig) {
        setDashboardConfig(normalizeDashboardConfig(config.dashboardConfig));
      }
      setIsAIGenerated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  async function loadDashboard(targetId: string) {
    setLoading(true);
    try {
      const data = await getReportDefinition(targetId);
      if (data) {
        setDashboard(data);
        setDashboardName(data.name);
        setDashboardDescription(data.description || '');
        setStatus((data.status as DocumentTemplateStatus) || 'draft');
        setIsInLibrary(Boolean(data.isInLibrary));
        setVisibility(data.visibility ?? 'all-departments');
        setAllowedTenantIds(data.allowedTenantIds ?? []);
        setDashboardConfig(normalizeDashboardConfig(data.dashboardConfig || DEFAULT_DASHBOARD_CONFIG));
        if (data.name) setBreadcrumbLabel(`/dashboard-templates/${targetId}`, data.name);
        setIsAIGenerated(data.aiGenerationSource === 'ai-generated');
      }
    } catch (error) {
      console.error('[SystemDashboardBuilderPage] Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleSelectWidget = useCallback((widgetId: string | null) => {
    setSelectedWidgetId(widgetId);
    if (widgetId) {
      setRightTab('properties');
      setRightPaneCollapsed(false);
    }
  }, []);

  const handleToggleActivation = useCallback((widgetId: string, enabled: boolean) => {
    setDashboardConfig((prev) => setWidgetEnabled(prev, widgetId, enabled));
  }, []);

  /**
   * @process handleSave — Persist system dashboard definition to `getSystemDb()`.
   */
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload: Partial<ReportDefinitionLocal> = {
        name: dashboardName || 'Untitled Dashboard',
        description: dashboardDescription,
        type: 'dashboard',
        status: status || 'draft',
        dashboardConfig,
        isInLibrary,
        visibility,
        allowedTenantIds: visibility === 'specific-tenants' ? allowedTenantIds : undefined,
        scope: dashboard?.scope || { level: 'system' },
        sharing: dashboard?.sharing || { sharedWith: [], isReadOnly: false },
        createdBy: dashboard?.createdBy || 'system-admin',
        updatedBy: 'system-admin',
        ...(isAIGenerated ? { aiGenerationSource: 'ai-generated' as const } : {}),
      };

      if (isNew) {
        const created = await createReportDefinition(payload);
        if (created?._id) {
          navigate(`/dashboard-templates/${created._id}`, { replace: true });
        }
      } else if (id) {
        const updated = await updateReportDefinition(id, payload);
        if (updated) setDashboard(updated);
      }
    } catch (error) {
      console.error('[SystemDashboardBuilderPage] Failed to save dashboard:', error);
    } finally {
      setSaving(false);
    }
  }, [
    dashboardName,
    dashboardDescription,
    status,
    dashboardConfig,
    isInLibrary,
    visibility,
    allowedTenantIds,
    dashboard,
    isAIGenerated,
    isNew,
    id,
    navigate,
  ]);

  /**
   * @process handlePublish — Publish system dashboard definition.
   */
  const handlePublish = useCallback(async () => {
    if (isNew || !id) return;
    setPublishing(true);
    try {
      await updateReportDefinition(id, {
        name: dashboardName || 'Untitled Dashboard',
        description: dashboardDescription,
        dashboardConfig,
      });
      const pub = await publishReportDefinition(id);
      if (pub) {
        setDashboard(pub);
        setStatus('published');
      } else {
        setStatus('published');
      }
    } catch (error) {
      console.error('[SystemDashboardBuilderPage] Failed to publish dashboard:', error);
    } finally {
      setPublishing(false);
    }
  }, [isNew, id, dashboardName, dashboardDescription, dashboardConfig]);

  /**
   * @process handleArchive — Archive system dashboard definition and remove from System Library.
   */
  const handleArchive = useCallback(async () => {
    if (isNew || !id) return;
    try {
      const arch = await archiveReportDefinition(id);
      if (arch) {
        setDashboard(arch);
        setStatus('archived');
        setIsInLibrary(false);
      } else {
        setStatus('archived');
        setIsInLibrary(false);
      }
    } catch (error) {
      console.error('[SystemDashboardBuilderPage] Failed to archive dashboard:', error);
    }
  }, [isNew, id]);

  /**
   * @process handleToggleLibrary — Toggle System Library exposure (`isInLibrary`).
   */
  const handleToggleLibrary = useCallback(
    async (checked: boolean) => {
      setIsInLibrary(checked);
      if (!isNew && id) {
        const updated = await toggleLibrary(id, checked);
        if (updated) {
          setDashboard(updated);
          setIsInLibrary(Boolean(updated.isInLibrary));
        }
      }
    },
    [isNew, id],
  );

  /**
   * @process handleVisibilityChange — Update System Library visibility mode.
   */
  const handleVisibilityChange = useCallback(
    async (nextVis: SystemLibraryVisibility) => {
      setVisibility(nextVis);
      if (!isNew && id) {
        await setReportDefinitionVisibility(id, nextVis, allowedTenantIds);
      }
    },
    [isNew, id, allowedTenantIds],
  );

  const handleFilterChange = useCallback((filterId: string, value: FilterValue | null) => {
    setGlobalFilters((prev) => ({ ...prev, [filterId]: value }));
    setRefreshKey((prev) => prev + 1);
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  // View / Fullscreen mode
  if (isViewMode) {
    return (
      <div
        data-testid={isFullscreen ? 'system-dashboard-fullscreen' : 'system-dashboard-view'}
        className={`h-full flex flex-col bg-gray-50 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}
      >
        {!isFullscreen && (
          <header className="shrink-0 flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="flex items-center gap-4">
              <button
                type="button"
                aria-label="Back to dashboard templates"
                onClick={() => navigate('/dashboard-templates')}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{dashboardName}</h1>
                {dashboardDescription && (
                  <p className="text-sm text-gray-500">{dashboardDescription}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(`/dashboard-templates/${id}/fullscreen`)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Maximize2 className="h-4 w-4" /> Fullscreen
              </button>
              <button
                type="button"
                onClick={() => navigate(`/dashboard-templates/${id}`)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Edit Dashboard
              </button>
            </div>
          </header>
        )}

        {isFullscreen && (
          <button
            type="button"
            aria-label="Exit fullscreen"
            onClick={() => navigate(`/dashboard-templates/${id}/view`)}
            className="fixed top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <main className="flex-1 min-h-0 overflow-auto">
          <DashboardCanvasBuilder
            config={dashboardConfig}
            onChange={setDashboardConfig}
            readOnly
            activationDriven
            widgetDataMap={widgetDataMap}
            refreshKey={refreshKey}
            filterValues={globalFilters}
            onFilterChange={handleFilterChange}
          />
        </main>
      </div>
    );
  }

  const canExposeInLibrary =
    status === 'published' && dashboardConfig.widgets.length > 0;

  return (
    <div
      data-testid="system-dashboard-builder-3pane"
      className="h-full flex flex-col bg-gray-50"
    >
      {/* Top Header */}
      <header className="shrink-0 flex items-center justify-between px-4 py-2.5 border-b bg-white gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            aria-label="Back to dashboard templates"
            onClick={() => navigate('/dashboard-templates')}
            className="p-2 rounded-lg hover:bg-gray-100 shrink-0"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="min-w-0">
            <input
              type="text"
              aria-label="Dashboard Name"
              value={dashboardName}
              onChange={(e) => setDashboardName(e.target.value)}
              className="text-base font-semibold bg-transparent border-none focus:outline-hidden focus:ring-2 focus:ring-blue-500 rounded-sm px-1 block w-72"
              placeholder="Dashboard Name"
            />
            <input
              type="text"
              aria-label="Dashboard Description"
              value={dashboardDescription}
              onChange={(e) => setDashboardDescription(e.target.value)}
              className="block text-xs text-gray-500 bg-transparent border-none focus:outline-hidden focus:ring-2 focus:ring-blue-500 rounded-sm px-1 w-72"
              placeholder="Add description..."
            />
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            data-testid="sys-builder-help-btn"
            onClick={() => setHelpOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <HelpCircle className="h-4 w-4" />
            Help
          </button>
          <button
            type="button"
            data-testid="sys-builder-tour-btn"
            onClick={() => setTourOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Compass className="h-4 w-4" />
            Tour
          </button>
          <button
            type="button"
            data-testid="sys-builder-grid-setup-btn"
            onClick={() => setGridSetupOpen((prev) => !prev)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border rounded-lg transition-colors ${
              gridSetupOpen
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Settings className="h-4 w-4" />
            Grid Setup
          </button>

          {!isNew && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/dashboard-templates/${id}/view`)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Play className="h-4 w-4" /> Preview
              </button>
              <button
                type="button"
                aria-label="Full-screen mode"
                onClick={() => navigate(`/dashboard-templates/${id}/fullscreen`)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
            </>
          )}

          <VisibilityBadge
            isInLibrary={isInLibrary}
            visibility={visibility}
            tenantCount={allowedTenantIds.length}
          />

          <DocumentLifecycleControls
            status={status}
            saving={saving}
            publishing={publishing}
            isViewMode={isViewMode}
            isNew={isNew}
            onSave={handleSave}
            onPublish={handlePublish}
            onArchive={handleArchive}
            accentColor="#2563eb"
            saveDataTour={DASHBOARD_TOUR_TARGET.savePublish}
          />
        </div>
      </header>

      {/* 3-Pane Body */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* LEFT PANE */}
        <aside
          data-testid="system-dashboard-builder-left-pane"
          style={{ width: leftPanelWidth }}
          className="shrink-0 bg-white border-r border-gray-200 overflow-y-auto flex flex-col"
        >
          {/* Widget Activation Checklist */}
          <div
            data-tour={DASHBOARD_TOUR_TARGET.activationList}
            data-testid="sys-widget-activation-list"
            className="p-3 border-b border-gray-200"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                Dashboard Widgets
              </span>
              <span className="text-[11px] text-gray-400">{activationEntries.length}</span>
            </div>
            <p className="text-[11px] text-gray-400 mb-2">Check to show · click row to edit</p>

            {activationEntries.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">No widgets on canvas yet.</p>
            ) : (
              <div className="space-y-1">
                {activationEntries.map((entry) => {
                  const active = selectedWidgetId === entry.id;
                  return (
                    <div
                      key={entry.id}
                      data-testid={`sys-activation-row-${entry.id}`}
                      onClick={() => handleSelectWidget(entry.id)}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-pointer transition-colors ${
                        active
                          ? 'bg-blue-50 text-blue-800 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Toggle ${entry.label}`}
                        data-testid={`sys-activation-checkbox-${entry.id}`}
                        checked={entry.enabled}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleToggleActivation(entry.id, e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
                      />
                      <span className="flex-1 truncate">{entry.label}</span>
                      <span className="text-[10px] uppercase text-gray-400">{entry.type}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* System Settings Accordions */}
          <div className="divide-y divide-gray-200">
            <div
              data-testid="system-library-visibility-accordion"
              data-tour={DASHBOARD_TOUR_TARGET.assignmentAccordion}
            >
              <AccordionSection title="Library & Visibility" defaultOpen>
                <div className="space-y-2.5 text-xs">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="expose-in-system-library-checkbox"
                      checked={isInLibrary}
                      disabled={!canExposeInLibrary}
                      onChange={(e) => void handleToggleLibrary(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <span className="font-medium text-gray-800">Expose in System Library</span>
                  </label>
                  {!canExposeInLibrary && (
                    <p className="text-[11px] text-gray-400">
                      Publish with at least 1 widget to expose in the System Library.
                    </p>
                  )}

                  <div className="pt-1 space-y-1.5">
                    <div className="text-[11px] font-medium text-gray-600 flex items-center gap-1">
                      <Globe className="h-3 w-3 text-blue-600" />
                      Visibility
                    </div>
                    {(
                      [
                        { id: 'all-departments', label: 'All Departments' },
                        { id: 'specific-tenants', label: 'Specific Tenants' },
                        { id: 'system-only', label: 'System Only' },
                      ] as Array<{ id: SystemLibraryVisibility; label: string }>
                    ).map((opt) => (
                      <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="system-dashboard-visibility"
                          data-testid={`visibility-radio-${opt.id}`}
                          checked={visibility === opt.id}
                          onChange={() => void handleVisibilityChange(opt.id)}
                          className="text-blue-600"
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>

                  {visibility === 'specific-tenants' && previewDepartments.length > 0 && (
                    <div
                      data-testid="specific-tenants-picker"
                      className="pt-2 border-t border-gray-100 space-y-1 max-h-36 overflow-y-auto"
                    >
                      <div className="text-[11px] font-medium text-gray-600">
                        Allowed Departments ({allowedTenantIds.length})
                      </div>
                      {previewDepartments.map((dept) => {
                        const checked = allowedTenantIds.includes(dept._id);
                        return (
                          <label key={dept._id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const nextIds = checked
                                  ? allowedTenantIds.filter((tid) => tid !== dept._id)
                                  : [...allowedTenantIds, dept._id];
                                setAllowedTenantIds(nextIds);
                                if (!isNew && id) {
                                  void setReportDefinitionVisibility(id, 'specific-tenants', nextIds);
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600"
                            />
                            <span className="truncate">{dept.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </AccordionSection>
            </div>

            <AccordionSection title="Auto-Refresh" defaultOpen>
              <div className="space-y-1.5">
                <label
                  htmlFor="sys-dash-auto-refresh"
                  className="block text-xs font-medium text-gray-600"
                >
                  Refresh Interval
                </label>
                <select
                  id="sys-dash-auto-refresh"
                  aria-label="Auto-refresh interval"
                  value={dashboardConfig.autoRefreshSeconds || 0}
                  onChange={(e) =>
                    setDashboardConfig((prev) => ({
                      ...prev,
                      autoRefreshSeconds: Number(e.target.value),
                    }))
                  }
                  className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md bg-white"
                >
                  <option value={0}>Off</option>
                  <option value={30}>30 seconds</option>
                  <option value={60}>1 minute</option>
                  <option value={300}>5 minutes</option>
                  <option value={900}>15 minutes</option>
                </select>
              </div>
            </AccordionSection>

            <AccordionSection title="Theme & Branding">
              <DashboardThemeEditor
                value={dashboardConfig.theme}
                onChange={(nextTheme) =>
                  setDashboardConfig((prev) => ({ ...prev, theme: nextTheme }))
                }
              />
            </AccordionSection>
          </div>
        </aside>

        {/* Left-pane drag resizer */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize left panel"
          data-testid="left-panel-resizer"
          onMouseDown={startResizeLeft}
          className="w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-blue-400/50 transition-colors"
        />

        {/* CENTER PANE: Shared DashboardCanvasBuilder */}
        <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
          <DashboardCanvasBuilder
            config={dashboardConfig}
            onChange={setDashboardConfig}
            selectedWidgetId={selectedWidgetId}
            onSelectedIdChange={handleSelectWidget}
            propertiesPortalTarget={propertiesHost}
            activationDriven
            widgetDataMap={widgetDataMap}
            refreshKey={refreshKey}
            filterValues={globalFilters}
            onFilterChange={handleFilterChange}
            onOpenHelp={() => setHelpOpen(true)}
            onStartTour={() => setTourOpen(true)}
          />
        </main>

        {/* Optional Grid Setup Drawer */}
        <GridSetupPanel
          open={gridSetupOpen}
          onClose={() => setGridSetupOpen(false)}
          value={dashboardConfig.layout}
          onChange={(nextLayout) =>
            setDashboardConfig((prev) => ({ ...prev, layout: nextLayout }))
          }
        />

        {/* Right-pane drag resizer */}
        {!rightPaneCollapsed && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize preview panel"
            data-testid="preview-resizer"
            onMouseDown={startResizeRight}
            className="w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-blue-400/50 transition-colors"
          />
        )}

        {/* RIGHT PANE: Live Preview (with Preview Department Selector) | Properties */}
        {rightPaneCollapsed ? (
          <div className="w-10 shrink-0 bg-white border-l border-gray-200 flex flex-col items-center py-3">
            <button
              type="button"
              aria-label="Expand right panel"
              onClick={() => setRightPaneCollapsed(false)}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
            >
              <PanelRightOpen className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <aside
            data-testid="system-dashboard-builder-right-pane"
            style={{ width: rightPanelWidth }}
            className="shrink-0 bg-white border-l border-gray-200 flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-3 pt-2 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-1" role="tablist">
                <button
                  type="button"
                  role="tab"
                  data-testid="right-tab-preview"
                  data-tour={DASHBOARD_TOUR_TARGET.previewTab}
                  aria-selected={rightTab === 'preview'}
                  onClick={() => setRightTab('preview')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-t-md border-b-2 transition-colors ${
                    rightTab === 'preview'
                      ? 'bg-white text-blue-600 border-blue-600'
                      : 'text-gray-600 border-transparent hover:text-gray-900'
                  }`}
                >
                  Live Preview
                </button>
                <button
                  type="button"
                  role="tab"
                  data-testid="right-tab-properties"
                  data-tour={DASHBOARD_TOUR_TARGET.propertiesTab}
                  aria-selected={rightTab === 'properties'}
                  onClick={() => setRightTab('properties')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-t-md border-b-2 transition-colors ${
                    rightTab === 'properties'
                      ? 'bg-white text-blue-600 border-blue-600'
                      : 'text-gray-600 border-transparent hover:text-gray-900'
                  }`}
                >
                  Properties
                </button>
              </div>
              <button
                type="button"
                aria-label="Collapse right panel"
                onClick={() => setRightPaneCollapsed(true)}
                className="p-1 rounded hover:bg-gray-200 text-gray-500 mb-1"
              >
                <PanelRightClose className="h-4 w-4" />
              </button>
            </div>

            {/* Live Preview Tab Body with Preview Department Selector */}
            <div
              data-testid="right-pane-preview-body"
              className={`flex-1 overflow-y-auto p-4 space-y-3 ${
                rightTab === 'preview' ? 'block' : 'hidden'
              }`}
            >
              <div className="p-2.5 rounded-lg bg-blue-50 border border-blue-200 space-y-1">
                <label
                  htmlFor="sys-preview-department-select"
                  className="block text-[11px] font-semibold text-blue-900"
                >
                  Preview Department
                </label>
                <select
                  id="sys-preview-department-select"
                  aria-label="Preview Department"
                  data-testid="preview-department-select"
                  value={previewDepartmentId}
                  onChange={(e) => setPreviewDepartmentId(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-blue-300 rounded bg-white"
                >
                  <option value={SAMPLE_PREVIEW_DEPARTMENT_ID}>Sample department</option>
                  {previewDepartments.map((d) => (
                    <option key={d._id} value={d._id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              {dashboardConfig.widgets.filter((w) => w.enabled !== false).length === 0 ? (
                <div className="p-6 text-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg">
                  Add or enable widgets to preview your dashboard.
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboardConfig.widgets
                    .filter((w) => w.enabled !== false)
                    .map((w) => (
                      <div
                        key={w.id}
                        className="p-3 rounded-lg border border-gray-200 bg-white shadow-2xs"
                      >
                        <div className="text-xs font-semibold text-gray-800 mb-2">{w.title}</div>
                        <div className="h-36">
                          <WidgetRenderer
                            widget={w}
                            data={widgetDataMap[w.id]}
                            refreshKey={refreshKey}
                            filterValues={globalFilters}
                            onFilterChange={handleFilterChange}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Properties Tab Body (Portal Target) */}
            <div
              ref={setPropertiesHost}
              data-testid="right-pane-properties-body"
              className={`flex-1 overflow-y-auto ${
                rightTab === 'properties' ? 'block' : 'hidden'
              }`}
            />
          </aside>
        )}
      </div>

      {/* Shared Help Panel & Guided Tour */}
      <BuilderHelpPanel
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onStartTour={() => {
          setHelpOpen(false);
          setTourOpen(true);
        }}
      />
      <GuidedTour
        active={tourOpen}
        steps={DASHBOARD_BUILDER_TOUR_STEPS}
        onComplete={() => setTourOpen(false)}
      />
    </div>
  );
}
